/**
 * Kivi drawn as operations, ported from the Kivi guide's model
 * (design/explorations/kivi/kivi-in-recordos.html: taper, eye, face, linePts,
 * discLine, discDots, fxOut and disc). The page builds SVG markup; this builds
 * the same shapes, in the same order, as a list of operations each renderer
 * paints. Path data keeps the page's two-decimal format.
 *
 * Every function is a worklet and reads only its arguments, its own file's
 * constants and other worklets, so the phone can run a frame on the UI thread.
 */
import {
  APP_ICON,
  BR,
  CURL_TO,
  DISC,
  E1,
  E2,
  FAVICON_DISC,
  FAVICON_EYE_STROKE,
  FAVICON_EYES,
  FAVICON_LINE,
  FS,
  GLOW,
  HIGHLIGHT,
  LINE,
  LIT_LIFT,
  PEBBLES,
  PIV,
  RIM,
  SHADOW_CY,
  SMALL_EYES,
  TAU,
  TICK_A,
  TICK_B,
} from './geometry';
import { bez, clamp, easeOut, f2, lerp, matMul, matRotate, matScale, matTranslate, tri } from './math';
import type {
  KiviCubic,
  KiviCut,
  KiviCutRect,
  KiviEyeMode,
  KiviEyes,
  KiviFrame,
  KiviFrameOptions,
  KiviFx,
  KiviLine,
  KiviMatrix,
  KiviOp,
  KiviPoint,
  KiviPose,
  KiviShape,
} from './types';

/** The options a taper stroke takes. */
export type KiviTaperOptions = {
  readonly t0?: number;
  readonly t1?: number;
  readonly wave?: number;
  readonly waves?: number;
  readonly phase?: number;
  readonly zig?: number;
  readonly zigs?: number;
  readonly jitter?: number;
  readonly seed?: number;
};

/**
 * A tapered pen stroke along a cubic, with optional wave, zigzag and tremble:
 * the outline, then a round cap at each end. Nothing when the span is too short.
 */
export function taper(P: KiviCubic, w0: number, w1: number, o: KiviTaperOptions = {}): KiviShape[] {
  'worklet';
  const t0 = o.t0 || 0;
  const t1 = o.t1 == null ? 1 : o.t1;
  if (t1 - t0 < 0.015) return [];
  const N = 36;
  const pts: KiviPoint[] = [];
  const L: KiviPoint[] = [];
  const R: KiviPoint[] = [];
  for (let i = 0; i <= N; i++) pts.push(bez(P, t0 + ((t1 - t0) * i) / N));
  for (let i = 0; i <= N; i++) {
    const p = pts[i] as KiviPoint;
    const a = pts[Math.max(i - 1, 0)] as KiviPoint;
    const b = pts[Math.min(i + 1, N)] as KiviPoint;
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const m = Math.hypot(dx, dy) || 1;
    const nx = -dy / m;
    const ny = dx / m;
    const g = t0 + ((t1 - t0) * i) / N;
    const w = lerp(w0, w1, i / N) / 2;
    let off = 0;
    if (o.wave) off += o.wave * Math.sin(g * TAU * (o.waves || 3) + (o.phase || 0));
    if (o.zig) off += o.zig * 2.2 * tri(g * (o.zigs || 5) + (o.phase || 0) / TAU);
    if (o.jitter) off += o.jitter * (Math.sin(g * 53 + (o.seed || 0) * 7.1) + Math.sin(g * 97 + (o.seed || 0) * 3.3)) * 0.9;
    off *= Math.min(1, g * 5);
    const cx = p[0] + nx * off;
    const cy = p[1] + ny * off;
    L.push([cx + nx * w, cy + ny * w]);
    R.push([cx - nx * w, cy - ny * w]);
  }
  let d = 'M';
  for (let i = 0; i <= N; i++) {
    const q = L[i] as KiviPoint;
    d += (i > 0 ? 'L' : '') + f2(q[0]) + ' ' + f2(q[1]);
  }
  for (let i = N; i >= 0; i--) {
    const q = R[i] as KiviPoint;
    d += 'L' + f2(q[0]) + ' ' + f2(q[1]);
  }
  d += 'Z';
  const l0 = L[0] as KiviPoint;
  const r0 = R[0] as KiviPoint;
  const lN = L[N] as KiviPoint;
  const rN = R[N] as KiviPoint;
  return [
    { kind: 'path', d },
    { kind: 'circle', cx: (l0[0] + r0[0]) / 2, cy: (l0[1] + r0[1]) / 2, r: w0 / 2 },
    { kind: 'circle', cx: (lN[0] + rN[0]) / 2, cy: (lN[1] + rN[1]) / 2, r: w1 / 2 },
  ];
}

/** The eye fields the disc sets from its cut, beside the pose's own. */
export type KiviEyesDrawn = KiviEyes & {
  readonly lidBias?: number;
  readonly dotK?: number;
  readonly arcW?: number;
  readonly clampX?: number;
};

/** A feature shape before the body transform: its paint, and a rotation about its own point. */
export type KiviFeature = {
  readonly shape: KiviShape;
  readonly stroke?: number;
  /** rotate(deg cx cy), or null for none. */
  readonly rotate: readonly [number, number, number] | null;
  /** A rectangle the shape is cut by, in the body's space. */
  readonly cut?: KiviCutRect;
  readonly opacity?: number;
};

/** A closed lid: two arcs, the lower one deep. */
export function lid(h: number | undefined, x: number, y: number, rot: number, e: KiviEyesDrawn): KiviFeature {
  'worklet';
  const s = e.size || 1;
  const hh = (h || 0) + (e.lidBias || 0);
  const w = 13.6 * FS * s;
  const d = (5.9 - hh * 1.6) * FS * s;
  const t = (1.3 + hh * 1.8) * s;
  return {
    shape: {
      kind: 'path',
      d:
        'M' + f2(x - w / 2) + ' ' + f2(y) +
        'A' + f2(w / 2) + ' ' + f2(Math.max(0.6, d)) + ' 0 0 0 ' + f2(x + w / 2) + ' ' + f2(y) +
        'A' + f2(w / 2) + ' ' + f2(Math.max(0.2, t)) + ' 0 0 1 ' + f2(x - w / 2) + ' ' + f2(y) + 'Z',
    },
    rotate: [rot, x, y],
  };
}

/**
 * One eye. side -1 is the near eye, 1 the far eye. Sizes are the pebble's,
 * times FS for the larger disc.
 */
export function eye(mode: KiviEyeMode, x: number, y: number, rot: number, e: KiviEyesDrawn, side: number): KiviFeature {
  'worklet';
  const s = e.size || 1;
  let open = e.open == null ? 1 : e.open;
  const aw = e.arcW || 3.4;
  if (side < 0 && e.openL != null) open *= e.openL;
  if (side > 0 && e.openR != null) open *= e.openR;
  if (mode === 'lid') return lid(e.heavy, x, y, rot, e);
  if (mode === 'arc') {
    const a = 10.6 * FS * s;
    return {
      shape: {
        kind: 'path',
        d: 'M' + f2(x - a / 2) + ' ' + f2(y + 1.4) + 'Q' + f2(x) + ' ' + f2(y - 5.6 * FS * s) + ' ' + f2(x + a / 2) + ' ' + f2(y + 1.4),
      },
      stroke: aw * Math.max(0.8, s),
      rotate: [rot, x, y],
    };
  }
  if (mode === 'flat') {
    return {
      shape: {
        kind: 'path',
        d: 'M' + f2(x - 3.8 * FS * s) + ' ' + f2(y + 1.6) + 'L' + f2(x + 3.8 * FS * s) + ' ' + f2(y + 1.6),
      },
      stroke: aw * 1.05,
      rotate: [rot, x, y],
    };
  }
  if (mode === 'dot') {
    return { shape: { kind: 'circle', cx: x, cy: y, r: 2.4 * FS * s * (e.dotK || 1) }, rotate: null };
  }
  if (mode === 'round') {
    if (open < 0.25) return lid(0, x, y, rot, e);
    const r = 4.6 * FS * Math.min(s, 1.1) * Math.min(1, open);
    return { shape: { kind: 'ellipse', cx: x, cy: y, rx: r, ry: r * 1.06 }, rotate: [rot, x, y] };
  }
  const W = 10.5 * FS * s;
  const H = 6.6 * FS * s * (e.h == null ? 1 : e.h) * open;
  if (H < 1.4) return lid(e.heavy, x, y, rot, e);
  const shape: KiviShape = {
    kind: 'path',
    d:
      'M' + f2(x - W / 2) + ' ' + f2(y) +
      'Q' + f2(x) + ' ' + f2(y - H * 1.15) + ' ' + f2(x + W / 2) + ' ' + f2(y) +
      'Q' + f2(x) + ' ' + f2(y + H * 0.95) + ' ' + f2(x - W / 2) + ' ' + f2(y) + 'Z',
  };
  const heavy = e.heavy || 0;
  if (heavy > 0) {
    const cut = rot + (e.cutIn || 0) * -side;
    const top = y - H * 1.15 * 0.62 + heavy * H * 1.25;
    return {
      shape,
      rotate: [rot, x, y],
      cut: { x: x - W, y: top, width: W * 2, height: 40, transform: matRotate(cut, x, y) },
    };
  }
  return { shape, rotate: [rot, x, y] };
}

/** Both eyes, near then far. pos: the two eye centres. */
export function face(e: KiviEyesDrawn, pos: readonly [KiviPoint, KiviPoint]): KiviFeature[] {
  'worklet';
  const out: KiviFeature[] = [];
  if (e.hide) return out;
  const lk = e.look || [0, 0];
  const sp = e.gap || 0;
  const off = e.off || [0, 0];
  for (let k = 0; k < 2; k++) {
    const p = k === 0 ? pos[0] : pos[1];
    const side = k === 0 ? -1 : 1;
    let x = p[0] + lk[0] + off[0] + side * sp;
    const y = p[1] + lk[1] + off[1];
    const rot = BR + (e.tiltIn || 0) * side;
    const mode: KiviEyeMode = side > 0 && e.mode2 ? e.mode2 : e.mode || 'lid';
    if (side > 0 && mode !== 'lid' && e.clampX != null) {
      const s = e.size || 1;
      const aw = e.arcW || 3.4;
      const hw =
        mode === 'round'
          ? 4.6 * FS * Math.min(s, 1.1)
          : mode === 'arc'
            ? 5.3 * FS * s + aw / 2
            : mode === 'flat'
              ? 3.8 * FS * s + aw / 2
              : mode === 'dot'
                ? 2.4 * FS * s
                : 5.25 * FS * s;
      x = Math.min(x, e.clampX - hw);
    }
    out.push(eye(mode, x, y, rot, e, side));
  }
  return out;
}

/** The line's cubic, lifted, drooped and curled. */
export function linePts(L: KiviLine): KiviCubic {
  'worklet';
  const lift = L.lift || 0;
  const dr = L.droop || 0;
  let p2: KiviPoint = [LINE[2][0], LINE[2][1] - lift * 6 + dr * 7];
  let p3: KiviPoint = [LINE[3][0] - dr * 10, LINE[3][1] - lift * 12 + dr * 20];
  if (L.curl) {
    const c = L.curl;
    p2 = [lerp(p2[0], CURL_TO[0][0], c), lerp(p2[1], CURL_TO[0][1], c)];
    p3 = [lerp(p3[0], CURL_TO[1][0], c), lerp(p3[1], CURL_TO[1][1], c)];
  }
  return [LINE[0], LINE[1], p2, p3];
}

/** The line out of the face, or the tick of done. */
export function discLine(L: KiviLine, cut: KiviCut): KiviFeature[] {
  'worklet';
  const out: KiviFeature[] = [];
  if (L.hide) return out;
  const k = (cut === 'small' ? 1.55 : cut === 'button' ? 1.15 : 1) * (L.w == null ? 1 : L.w);
  let shapes: KiviShape[] = [];
  if (L.tick != null && L.tick > 0) {
    const p = L.tick;
    if (1 - p > 0.12) shapes = shapes.concat(taper(linePts({}), 8 * k, 5.8 * k, { t0: cut === 'small' ? 0.2 : 0.03, t1: 1 - p }));
    shapes = shapes.concat(taper(TICK_A, 8 * k, 7.6 * k, { t1: clamp(p / 0.3) }));
    if (p > 0.3) shapes = shapes.concat(taper(TICK_B, 7.6 * k, 5.8 * k, { t1: clamp((p - 0.3) / 0.7) }));
  } else {
    const P = linePts(L);
    const t0 = L.t0 == null ? (cut === 'small' ? 0.2 : 0.03) : L.t0;
    const a0 = (L.w0 || 6.4) / 6.4;
    const a1 = (L.w1 || 4.4) / 4.4;
    const big = cut === 'small' ? 3.2 : cut === 'button' ? 2 : 1.3;
    shapes = taper(P, 8 * k * a0, 5.8 * k * a1, {
      t0,
      t1: L.t1 == null ? 1 : L.t1,
      wave: L.wave ? L.wave * big : 0,
      waves: L.waves,
      phase: L.phase,
      zig: L.zig ? L.zig * big : 0,
      zigs: L.zigs,
      jitter: L.jitter ? L.jitter * 1.25 : 0,
      seed: L.seed,
    });
  }
  for (let i = 0; i < shapes.length; i++) out.push({ shape: shapes[i] as KiviShape, rotate: null });
  return out;
}

/** The three dots that travel out along the line while thinking. */
export function discDots(L: KiviLine, cut: KiviCut): KiviFeature[] {
  'worklet';
  const out: KiviFeature[] = [];
  if (!L.dots) return out;
  const P = linePts(L);
  const t1 = L.t1 == null ? 0.3 : L.t1;
  const r = cut === 'small' ? 4.4 : cut === 'button' ? 3.8 : 3.1;
  for (let i = 0; i < 3; i++) {
    const u = (i / 3 + (L.dotP || 0)) % 1;
    const q = bez(P, t1 + 0.08 + u * (1.0 - t1));
    out.push({ shape: { kind: 'circle', cx: q[0], cy: q[1], r }, rotate: null, opacity: Math.sin(Math.PI * u) });
  }
  return out;
}

/** The record pebbles of building, stacking at the right, outside the body's transform. */
export function pebbles(fx: KiviFx | null): KiviOp[] {
  'worklet';
  const out: KiviOp[] = [];
  if (!fx || fx.pebbles == null) return out;
  const n = fx.pebbles;
  for (let j = 0; j < 3; j++) {
    const q = clamp(n - j);
    if (q <= 0) continue;
    const dy = (1 - easeOut(q)) * -24;
    out.push({
      kind: 'path',
      d: PEBBLES[j] as string,
      paint: 'fill',
      strokeWidth: 0,
      role: j === 0 ? 'pebble1' : j === 1 ? 'pebble2' : 'pebble3',
      opacity: clamp(q * 3) * (fx.op == null ? 1 : fx.op),
      transform: matTranslate((fx.px || 0) + 6, 6 + dy),
      clip: false,
    });
  }
  return out;
}

/** The body's transform: lean on the pivot, then squash about the floor point. */
export function bodyMatrix(P: KiviPose): KiviMatrix {
  'worklet';
  const piv = PIV[P.piv || 'front'] || PIV.front;
  let m = matTranslate(P.tx || 0, P.ty || 0);
  m = matMul(m, matRotate(P.rot || 0, piv[0], piv[1]));
  m = matMul(m, matTranslate(50, 94));
  m = matMul(m, matScale(P.sx || 1, P.sy || 1));
  return matMul(m, matTranslate(-50, -94));
}

/** A feature as an operation: the body's transform, then its own rotation. */
function featureOp(f: KiviFeature, role: KiviOp['role'], body: KiviMatrix, clip: boolean, layer: number | undefined): KiviOp {
  'worklet';
  const transform = f.rotate ? matMul(body, matRotate(f.rotate[0], f.rotate[1], f.rotate[2])) : body;
  const op: KiviOp = {
    ...f.shape,
    paint: f.stroke != null ? 'stroke' : 'fill',
    strokeWidth: f.stroke != null ? f.stroke : 0,
    ...(f.stroke != null ? { cap: 'round' as const } : {}),
    role,
    opacity: f.opacity == null ? 1 : f.opacity,
    ...(layer != null ? { layer } : {}),
    transform,
    clip,
    ...(f.cut ? { cutRect: { ...f.cut, transform: matMul(body, f.cut.transform) } } : {}),
  };
  return op;
}

/**
 * How much of the face a frame shows, 0..1 (`KiviFrameOptions.face`): the
 * amount when given, else the faceless switch, true 0 and false 1.
 */
export function kiviFaceAmountOf(opts: Pick<KiviFrameOptions, 'face' | 'faceless'>): number {
  'worklet';
  if (opts.face != null) return clamp(opts.face);
  return opts.faceless ? 0 : 1;
}

/** A face feature at a face amount: its own opacity times the amount; whole at 1. */
function faded(f: KiviFeature, amount: number): KiviFeature {
  'worklet';
  if (amount >= 1) return f;
  return { ...f, opacity: (f.opacity == null ? 1 : f.opacity) * amount };
}

/**
 * One frame of Kivi as operations in paint order: the glow, the floor shadow,
 * the dots behind, the body, the lit body and highlight (clipped), the rim, the
 * eyes, line and dots (clipped), then the record pebbles.
 */
export function kiviFrame(P: KiviPose, opts: KiviFrameOptions = {}): KiviFrame {
  'worklet';
  const cut: KiviCut = opts.cut || 'full';
  const rev = !!opts.rev;
  const lit = !!opts.lit && !rev;
  const main: KiviOp['role'] = rev ? 'feature' : 'body';
  const feat: KiviOp['role'] = rev ? 'body' : 'feature';
  const shows = kiviFaceAmountOf(opts);
  const faceless = shows <= 0;
  const fading = shows < 1 && !faceless;
  const shown: KiviEyes = P.eyes || { mode: 'lid' };
  // A face fading narrows its eyes toward half open as it goes: the lids close a little.
  const lidded: KiviEyes = fading ? { ...shown, open: (shown.open == null ? 1 : shown.open) * (0.5 + 0.5 * shows) } : shown;
  const base: KiviEyes = faceless ? { ...shown, hide: true } : lidded;
  const e: KiviEyesDrawn = {
    ...base,
    ...(cut === 'small' ? { lidBias: -0.6, dotK: 1.3 } : {}),
    ...(cut === 'button' ? { size: (base.size || 1) * 1.06 } : {}),
    arcW: cut === 'small' ? 8.4 : cut === 'button' ? 5.2 : 3.4,
    clampX: cut === 'small' ? 66.4 : 68.8,
  };
  const ops: KiviOp[] = [];
  const sx = P.sx || 1;
  if (P.glow) {
    ops.push({
      kind: 'circle', cx: GLOW.cx, cy: GLOW.cy, r: GLOW.r,
      paint: 'fill', strokeWidth: 0, role: 'glow', opacity: GLOW.opacity * P.glow, transform: null, clip: false,
    });
  }
  if (opts.shadow !== false && !rev) {
    const lift = Math.min(0, P.ty || 0);
    ops.push({
      kind: 'ellipse', cx: 50 + (P.tx || 0), cy: SHADOW_CY, rx: 30 * sx * (1 + lift / 70), ry: 2.6,
      paint: 'fill', strokeWidth: 0, role: 'shadow', opacity: (lit ? 0.14 : 0.08) * (1 + lift / 45), transform: null, clip: false,
    });
  }
  const body = bodyMatrix(P);
  const line: KiviLine = faceless ? { hide: true } : P.line || {};
  if (line.dots) {
    const dots = discDots(line, cut);
    for (let i = 0; i < dots.length; i++) ops.push(featureOp(faded(dots[i] as KiviFeature, shows), main, body, false, undefined));
  }
  ops.push({ kind: 'path', d: DISC, paint: 'fill', strokeWidth: 0, role: lit ? 'under' : main, opacity: 1, transform: body, clip: false });
  if (lit) {
    ops.push({
      kind: 'path', d: DISC, paint: 'fill', strokeWidth: 0, role: 'body', opacity: 1,
      transform: matMul(body, matTranslate(LIT_LIFT[0], LIT_LIFT[1])), clip: true,
    });
    ops.push({
      kind: 'ellipse', cx: HIGHLIGHT.cx, cy: HIGHLIGHT.cy, rx: HIGHLIGHT.rx, ry: HIGHLIGHT.ry,
      paint: 'fill', strokeWidth: 0, role: 'light', opacity: HIGHLIGHT.opacity,
      transform: matMul(body, matRotate(HIGHLIGHT.rot, HIGHLIGHT.cx, HIGHLIGHT.cy)), clip: true,
    });
  }
  if (opts.rim) {
    ops.push({ kind: 'path', d: DISC, paint: 'stroke', strokeWidth: RIM.width, role: 'light', opacity: RIM.opacity, transform: body, clip: false });
  }
  const eyes = face(e, cut === 'small' ? SMALL_EYES : [E1, E2]);
  for (let i = 0; i < eyes.length; i++) ops.push(featureOp(faded(eyes[i] as KiviFeature, shows), feat, body, true, undefined));
  const strokes = discLine(line, cut);
  // The line's strokes overlap at their caps, so a fading line is one layer, as silent's faded line is.
  const alpha = (line.alpha == null ? 1 : line.alpha) * shows;
  const layer = fading ? alpha : line.alpha != null && line.alpha < 1 ? line.alpha : undefined;
  for (let i = 0; i < strokes.length; i++) ops.push(featureOp(strokes[i] as KiviFeature, feat, body, true, layer));
  if (line.dots) {
    const dots = discDots(line, cut);
    for (let i = 0; i < dots.length; i++) ops.push(featureOp(faded(dots[i] as KiviFeature, shows), feat, body, true, undefined));
  }
  const peb = pebbles(P.fx);
  for (let i = 0; i < peb.length; i++) ops.push(peb[i] as KiviOp);
  return { ops, clip: { d: DISC, transform: body } };
}

/**
 * How a renderer mixes the body and under colours from the pose's tint, as the
 * page's mixc does. Each is `t` of the way from the `from` token to the `to`
 * token. `night` is the page's #0E0F4F, below deep; it is not a theme token,
 * and no app state tints below 0.
 */
export type KiviTintMix = {
  readonly body: { readonly from: 'lapis'; readonly to: 'light' | 'deep'; readonly t: number };
  readonly under: { readonly from: 'deep'; readonly to: 'lapis' | 'night'; readonly t: number };
};

export function kiviTintMix(P: Pick<KiviPose, 'tint'>): KiviTintMix {
  'worklet';
  const tint = P.tint || 0;
  return tint >= 0
    ? { body: { from: 'lapis', to: 'light', t: tint }, under: { from: 'deep', to: 'lapis', t: tint * 0.5 } }
    : { body: { from: 'lapis', to: 'deep', t: -tint }, under: { from: 'deep', to: 'night', t: -tint * 0.6 } };
}

/**
 * The favicon drawing (`FAVICON_*`, the guide's `favicon()`) as a frame: the
 * disc in the body's colour, the lids and line in the features'. `rev` is the
 * paper version for a dark tab: the disc in the features' colour, the face in
 * the body's.
 */
export function kiviFaviconFrame(rev?: boolean): KiviFrame {
  'worklet';
  const main: KiviOp['role'] = rev ? 'feature' : 'body';
  const feat: KiviOp['role'] = rev ? 'body' : 'feature';
  const ops: KiviOp[] = [{ kind: 'path', d: FAVICON_DISC, paint: 'fill', strokeWidth: 0, role: main, opacity: 1, transform: null, clip: false }];
  for (let i = 0; i < FAVICON_EYES.length; i++) {
    const e = FAVICON_EYES[i] as (typeof FAVICON_EYES)[number];
    const m = matRotate(e.rotate[0], e.rotate[1], e.rotate[2]);
    ops.push({ kind: 'path', d: e.d, paint: 'fill', strokeWidth: 0, role: feat, opacity: 1, transform: m, clip: true });
    ops.push({ kind: 'path', d: e.d, paint: 'stroke', strokeWidth: FAVICON_EYE_STROKE, join: 'round', role: feat, opacity: 1, transform: m, clip: true });
  }
  ops.push({ kind: 'path', d: FAVICON_LINE, paint: 'fill', strokeWidth: 0, role: feat, opacity: 1, transform: null, clip: true });
  return { ops, clip: { d: FAVICON_DISC, transform: [1, 0, 0, 1, 0, 0] } };
}

/**
 * The app icon (the guide's `appIcon()`) as a frame, in a 100 box: the tile
 * in the body's colour, then Kivi at rest as the paper disc (`rev`), with no
 * floor shadow, moved and scaled onto it. `square` draws the tile without
 * its rounded corners, for a platform that rounds it (iOS's touch icon).
 */
export function kiviAppIconFrame(square?: boolean): KiviFrame {
  'worklet';
  const r = square ? 0 : APP_ICON.radius;
  const s = f2(r);
  const e = f2(100 - r);
  const tile =
    r === 0
      ? 'M0 0H100V100H0Z'
      : 'M' + s + ' 0H' + e + 'A' + s + ' ' + s + ' 0 0 1 100 ' + s + 'V' + e + 'A' + s + ' ' + s + ' 0 0 1 ' + e + ' 100H' + s +
        'A' + s + ' ' + s + ' 0 0 1 0 ' + e + 'V' + s + 'A' + s + ' ' + s + ' 0 0 1 ' + s + ' 0Z';
  const place = matMul(matTranslate(APP_ICON.x, APP_ICON.y), matScale(APP_ICON.scale, APP_ICON.scale));
  const rest: KiviPose = { rot: 0, piv: 'front', tx: 0, ty: 0, sx: 1, sy: 1, tint: 0, eyes: { mode: 'lid' }, line: {}, glow: 0, fx: null };
  const disc = kiviFrame(rest, { rev: true, shadow: false });
  const ops: KiviOp[] = [{ kind: 'path', d: tile, paint: 'fill', strokeWidth: 0, role: 'body', opacity: 1, transform: null, clip: false }];
  for (let i = 0; i < disc.ops.length; i++) {
    const op = disc.ops[i] as KiviOp;
    ops.push({
      ...op,
      transform: matMul(place, op.transform || [1, 0, 0, 1, 0, 0]),
      ...(op.cutRect ? { cutRect: { ...op.cutRect, transform: matMul(place, op.cutRect.transform) } } : {}),
    });
  }
  return { ops, clip: { d: disc.clip.d, transform: matMul(place, disc.clip.transform) } };
}
